import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { CollectionProgramAsset, useGridSettings, useSeriesCatchUpGridCollection } from '@kaltura-ott/tvpil-shared';

import { COMPONENT_NAME, PAGE_SIZE } from 'components/pages/Grids/constants';
import { useGridMatchPath } from 'components/pages/Grids/hooks/useGridMatchPath/useGridMatchPath';
import { DEFAULT_IMAGE_PARAMS } from 'consts';
import { WidgetProviderData } from 'context/widgetProvider/types';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';
import routes from 'routesPaths';
import { applyStyleHexToHSL, applyStyles } from 'utils';

function useSeriesCatchUpGrid() {
  const { type, id } = useGridMatchPath(routes.seriesCatchUpGrid.path);
  const [, setForceUpdate] = useState(false);

  const settings = useGridSettings({ gridReferenceId: type, railReferenceId: id, ...DEFAULT_IMAGE_PARAMS });

  const { items, totalCount, isLoading, hasNextPage, fetchNextPage, error, refetch } = useSeriesCatchUpGridCollection({
    gridReferenceId: type,
    railReferenceId: id,
    pageSize: PAGE_SIZE,
  });

  const data: WidgetProviderData<CollectionProgramAsset> = {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };

  const wrapperRef = useRef(null);
  const intl = useIntl();
  const notValidPageTitle = intl.formatMessage({
    id: `${COMPONENT_NAME}.notValidPage`,
    defaultMessage: 'Not a valid page',
  });

  useEffect(() => {
    async function initGridPage() {
      const node = wrapperRef?.current;
      if (!settings) return;

      if (node && settings.branding) {
        applyStyles(node, settings.branding, false, true);
        applyStyleHexToHSL(node, settings.branding.backgroundColor);
      }
    }

    if (settings) {
      initGridPage();
    }
  }, [type, id, settings]);

  useRhinoErrorReaction({ error, refetch });

  return {
    data,
    settings,
    wrapperRef,
    setForceUpdate,
    notValidPageTitle,
  };
}

export { useSeriesCatchUpGrid };
